import * as React from "react";
import { WithStyles, withStyles, createStyles, Theme } from 'components/controls/styles';
import { withRxForm, RxFormProps, RxFormFieldDefs, Props as RxProps } from "components/rx-form";
import { buildValidator } from "components/validators";
import { Button, DialogActions, DialogContent, Grid, Typography } from "components/controls/widget";
import ConfirmDialog from "common/components/controls/ConfirmDialog";
import Loading from "common/components/controls/Loading";
import Error from "common/components/controls/Error";
import { FormSpecification, FormResponse } from "common/api/Forms";
import buildFormControl from "./buildFormControl";

interface Props {
    uploadFileUrl: string;
    formSpec: FormSpecification;
    loading: boolean;
    hasError: boolean;
    error?: string;
    formResp?: FormResponse;
    cancel: () => void;
}

export interface VotingFormProps extends Props {
    fields: RxFormFieldDefs;
    disableValidation?: boolean;
}

type PropsType = Props & RxFormProps & WithStyles<typeof styles>;

interface State {
    showConfirmDialog: boolean;
    formEvent?: React.FormEvent<Element>;
}

class VotingForm extends React.PureComponent<PropsType, State> {

    constructor(props: PropsType) {
        super(props);

        this.state = {
            showConfirmDialog: false
        }
    }

    submit = (e: React.FormEvent<Element>, completed: boolean) => {
        const { fields, handleSubmit } = this.props;
        fields['completed'].value = completed;

        if (completed) {
            e.persist();
            this.setState({ showConfirmDialog: true, formEvent: e });
        } else {
            handleSubmit(e, completed === false);
        }
    }

    handleConfirm = (confirm: boolean) => {
        this.setState({ showConfirmDialog: false, formEvent: undefined });

        if (confirm) {
            this.props.handleSubmit(this.state.formEvent, true);
        }
    }

    getConfirmDialogContent = () => {
        return <div>
            <Typography variant="h6">{'Opravdu chcete úpravy ukončit a hlasování odeslat?'}</Typography>
            <Typography variant="body1">{'Po odeslání se hlasování uzavře a již ho nebudete moci změnit.'}</Typography>
        </div>
    }

    setTypHlasovani = () => {
        const { fields, formSpec } = this.props;

        if (fields['typ_hlasovani'] !== undefined) {
            if (formSpec.specification.cisloDivize === 51) {
                const plneMoci = fields['typ_hlasovani'].value === "1";
                fields['plne_moci'].disabled = plneMoci === false;
                fields['doklad_totoznosti'].disabled = plneMoci === false;
            } else {
                const plneMoci = fields['typ_hlasovani'].value === "1" || fields['typ_hlasovani'].value === "2";
                fields['plne_moci'].disabled = plneMoci === false;
                fields['pocet_hlasu'].disabled = fields['typ_hlasovani'].value !== "1";
            }
        }

    }

    render() {

        const { uploadFileUrl, cancel, loading, hasError, error, fields, classes, submitting, globalError } = this.props;

        this.setTypHlasovani();

        return <React.Fragment>
            <DialogContent className={classes.contentRoot}>
                {loading
                    ? <Loading loading={loading} color="primary" />
                    : hasError
                        ? <Error>{error}</Error>
                        : <form>
                            <Grid container direction="column" spacing={24}>
                                {this.props.formSpec.specification.fields.map(f => {
                                    const control = buildFormControl(f, this.props.fields[f.name], uploadFileUrl, fields);
                                    return control ? <Grid key={f.name} item className={classes.item}>{control}</Grid> : null;
                                })}
                            </Grid>
                        </form>
                }
            </DialogContent>
            <DialogActions className={classes.actionsRoot}>
                <Grid container direction="column" spacing={8}>
                    {globalError && <Grid item>
                        <Error noPadding>{globalError}</Error>
                    </Grid>}
                    <Grid item>

                        <Grid container direction="row" spacing={8}>
                            <Grid item>
                                <Button type="submit" variant="contained" color="primary" disabled={submitting || loading} onClick={e => this.submit(e, false)}>{'Uložit rozpracovaná data a jít zpět'}</Button>
                            </Grid>
                            <Grid item>
                                <Button type="submit" variant="contained" color="success" disabled={submitting || loading} onClick={e => this.submit(e, true)}>{'Ukončit úpravy a odeslat hlasování'}</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogActions>
            <ConfirmDialog show={this.state.showConfirmDialog} title="Ukončit úpravy a odeslat hlasování" content={this.getConfirmDialogContent} onResult={this.handleConfirm} />
        </React.Fragment>;
    }
}


const styles = (theme: Theme) => createStyles({
    item: {
        border: '1px solid silver',
        borderRadius: 5,
        marginBottom: 3,
    },
    contentRoot: {
        paddingTop: theme.spacing.unit * 2,
    },
    actionsRoot: {
        borderTop: `1px solid ${theme.palette.divider}`,
        margin: 0,
        padding: theme.spacing.unit,
    },
});


export default withStyles(styles)(((Component: React.ComponentType<RxFormProps>) => {
    return class extends React.Component<VotingFormProps & RxProps> {
        render() {
            return React.createElement(withRxForm(this.props.fields, buildValidator(this.props.fields))(Component), { ...this.props });
        }
    };
})(VotingForm));